import { recommendWhoDrugTool } from "./recommendWhoDrug";
import type { Tool } from "@ag-ui/client";
import type { CaseReviewAgentBindings } from "../shared/model/caseReviewAgentBindings";
import type { PageLocation } from "../shared/model/pageLocation";
import type { WorkQueueAgentBindings } from "../shared/model/workQueueAgentBindings";
import { closePanelTool } from "./closePanel";
import { goToFieldTool } from "./goToField";
import { highlightSourceTool } from "./highlightSource";
import { navigateToTool } from "./navigateTo";
import { openPanelTool } from "./openPanel";
import { proposeFieldValueTool } from "./proposeFieldValue";
import { setFilterTool } from "./setFilter";
import { FRONTEND_TOOL_CODES, FRONTEND_TOOL_NAMES } from "./frontendToolNames";
import { refuse, type FrontendTool, type FrontendToolArgs, type FrontendToolEnvironment, type FrontendToolResult } from "./frontendTool";

/** The frontend tools, in declaration order. */
const FRONTEND_TOOLS: FrontendTool[] = [recommendWhoDrugTool, navigateToTool, goToFieldTool, proposeFieldValueTool, highlightSourceTool, openPanelTool, closePanelTool, setFilterTool];

/** The declarations to send with a run: the tools this screen offers, with their enums as of now. */
export function declarationsFor(location: PageLocation | null, caseReview: CaseReviewAgentBindings | null,
                                workQueue: WorkQueueAgentBindings | null): Tool[] {
  return FRONTEND_TOOLS.flatMap((tool) => {
    if (caseReview?.whoDrug && tool.name === FRONTEND_TOOL_NAMES.proposeFieldValue) return [];
    const declaration = tool.declaration(location, caseReview, workQueue);
    return declaration === null ? [] : [declaration];
  });
}

/** Whether a call names a tool this run declared — the only tools the browser executes. */
export function isFrontendTool(name: string, declared: Tool[]): boolean {
  return declared.some((tool) => tool.name === name);
}

/**
 * Runs the named tool. A name nobody declared answers `UNKNOWN_TOOL`; an executor that throws
 * answers `TOOL_FAILED` with the message, so the run continues and the reader sees the failure in
 * the trail rather than a dead stream.
 */
export async function dispatch(name: string, args: FrontendToolArgs, env: FrontendToolEnvironment): Promise<FrontendToolResult> {
  if (env.caseReview?.whoDrug && name === FRONTEND_TOOL_NAMES.proposeFieldValue) {
    return refuse(FRONTEND_TOOL_CODES.outOfScope, "WHODrug entries must use recommendWhoDrug and the recode comparison, not individual field proposals.");
  }
  const tool = FRONTEND_TOOLS.find((candidate) => candidate.name === name);
  if (tool === undefined) return refuse(FRONTEND_TOOL_CODES.unknownTool, `${name} is not a tool this screen offers.`);
  try {
    return await tool.execute(args, env);
  } catch (error: unknown) {
    console.error(`Frontend tool ${name} failed`, error);
    return refuse(FRONTEND_TOOL_CODES.failed, error instanceof Error ? error.message : String(error));
  }
}
